import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Center,
  HStack,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import Card from "components/card/Card.js";
import { AuditEvent, useAdminAuditEvents } from "api/admin";

const PAGE_SIZE = 10;
type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function truncate(value?: string | null, maxLength = 54): string {
  if (!value) return "—";
  return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
}

export default function Security(): JSX.Element {
  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState("");
  const [event, setEvent] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError } = useAdminAuditEvents({
    page,
    limit: PAGE_SIZE,
    userId: userId || undefined,
    event: event || undefined,
    from: from || undefined,
    to: to || undefined,
  });

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card p="20px" mb="20px">
        <VStack align="stretch" spacing="16px">
          <Heading size="md">Security Audit Trail</Heading>
          <HStack spacing={3} flexWrap="wrap">
            <Input maxW="240px" placeholder="User ID" value={userId} onChange={(e: InputChangeEvent) => { setPage(1); setUserId(e.target.value); }} />
            <Input maxW="220px" placeholder="Event e.g. login" value={event} onChange={(e: InputChangeEvent) => { setPage(1); setEvent(e.target.value); }} />
            <Input maxW="180px" type="date" value={from} onChange={(e: InputChangeEvent) => { setPage(1); setFrom(e.target.value); }} />
            <Input maxW="180px" type="date" value={to} onChange={(e: InputChangeEvent) => { setPage(1); setTo(e.target.value); }} />
          </HStack>
        </VStack>
      </Card>

      <Card p="20px">
        {isLoading ? (
          <Center p="40px"><Spinner /></Center>
        ) : isError ? (
          <Center p="40px"><Text color="red.500">Failed to load audit events.</Text></Center>
        ) : !data?.data?.length ? (
          <Center p="40px"><Text>No audit events found.</Text></Center>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Time</Th>
                  <Th>Event</Th>
                  <Th>User</Th>
                  <Th>IP</Th>
                  <Th>User Agent</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.data.map((auditEvent: AuditEvent) => (
                  <Tr key={auditEvent.id}>
                    <Td whiteSpace="nowrap">{formatDate(auditEvent.createdAt)}</Td>
                    <Td><Badge bg="#FAB643" color="#131313">{auditEvent.event}</Badge></Td>
                    <Td>{auditEvent.user ? `${auditEvent.user.fullName} (${auditEvent.user.email})` : auditEvent.userId || "—"}</Td>
                    <Td>{auditEvent.ip || "—"}</Td>
                    <Td title={auditEvent.userAgent || undefined}>{truncate(auditEvent.userAgent)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        <HStack mt="20px" justify="space-between">
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>Previous</Button>
          <Text>Page {data?.page || page} of {data?.totalPage || 1}</Text>
          <Button onClick={() => setPage((p) => p + 1)} isDisabled={!data || page >= (data.totalPage || 1)}>Next</Button>
        </HStack>
      </Card>
    </Box>
  );
}
